<template>
  <div v-if="hasFields" :class="classes">
    <h2
      v-if="hasFields && headingKey"
      ref="label"
      class="m-metadata-container__heading"
    >
      {{ translate(headingKey) }}
    </h2>

    <template v-for="(value, key) in metadataFields" :key="key">
      <RecordMetadataField
        v-if="value != undefined && value?.length > 0"
        ref="field"
        :classes="getClasses(value)"
        :data-testid="key"
        :label="getLabel(key)"
        :modifier="modifier"
        :base-locale-key="baseLocaleKey"
        label-suffix=""
      >
        <template v-for="(val, index) in getValues(value)" :key="index">
          <component :is="val.is" v-bind="val" />
        </template>
      </RecordMetadataField>
    </template>
  </div>
</template>

<script>
import {
  DelimitedFieldValues,
  localizationMixin,
  RecordMetadataField
} from "@thegetty-private/getty-ui";
import RichTextWrapper from "./richTextWrapper.vue";
import { getValueOrContent } from "@thegetty/linkedart.js";
import { getClassifiedAs } from "@thegetty/linkedart.js";
import aat from "@/data/aat.json";

export default {
  name: "MetadataGroup",
  components: {
    DelimitedFieldValues,
    RecordMetadataField,
    RichTextWrapper
  },
  mixins: [localizationMixin],
  provide() {
    return {
      /**
       * Options for the markdown renderer used in RichText in DelimitedFieldValues
       */
      delimitedValuesMarkdownOptions: {
        breaks: true
      }
    };
  },
  inheritAttrs: false,
  props: {
    /**
     * The base locale key for localization
     */
    baseLocaleKey: {
      required: false,
      default: "m-record-metadata-field",
      type: String
    },
    /**
     * An object of Key/value pairs display in the metadata block
     */
    metadataFields: {
      required: true,
      type: Object
    },
    /**
     * Modifier for RecordMetadataField
     */
    modifier: {
      type: String,
      default: undefined
    },
    /**
     * The localization key for the heading
     */
    headingKey: {
      type: String,
      required: false,
      default: undefined
    },
    /**
     * a css variation
     */
    variation: {
      type: String,
      required: false,
      default: undefined
    }
  },
  computed: {
    /**
     * The current set of classes
     *
     * @returns {array}
     */
    classes() {
      let toReturn = ["m-metadata"];
      if (this.variation) {
        toReturn.push(`m-metadata--${this.variation}`);
      }
      if (this.headingKey) {
        toReturn.push("m-metadata--with-heading");
      }
      return toReturn;
    },
    /**
     * Whether there are any fields to display
     *
     * @returns {boolean}
     */
    hasFields() {
      if (this.metadataFields == undefined) {
        return false;
      }

      if (Object.keys(this.metadataFields).length < 1) {
        return false;
      }
      for (let key of Object.keys(this.metadataFields)) {
        if (this.metadataFields[key] == undefined) {
          continue;
        }

        if (
          this.metadataFields[key] != "" ||
          this.metadataFields[key].length > 0
        ) {
          return true;
        }
      }
      return false;
    }
  },
  methods: {
    /**
     * prepares the value(s) to be props
     *
     * @param {String|Array|Object|Boolean|Number} - value
     *
     * @returns {object}
     */
    prepareValuesAsProps(value) {
      if (value?.contentType) {
        let content = getValueOrContent(value);
        if (value.contentType == "text/html") {
          // if we have text/html, render newlines better
          content = content.replaceAll(/\n/g, "<br/>");
        }
        return {
          content: content,
          is: "RichTextWrapper"
        };
      }
      return {
        values: value,
        is: "DelimitedFieldValues",
        delimiter: "block"
      };
    },
    /**
     * Gets the values ready for rendering
     *
     * @param {String|Array|Object|Boolean|Number} value - the value or value(s) to process
     *
     * @returns {Array} - the values to render
     */
    getValues(value) {
      if (value == undefined) {
        return [];
      }

      // if we don't have an array, just handle the value
      if (Array.isArray(value) == false) {
        return [this.prepareValuesAsProps(value)];
      }

      //check if we have something that has a contentType
      let hasRichText = value.filter((v) => v?.contentType).length > 0;

      // if values is an array and none of them have a contentType ... then use delimitedFieldValues
      if (hasRichText == false) {
        return [this.prepareValuesAsProps(value)];
      }

      // otherwise pass to a RichText component
      return value.map((val) => this.prepareValuesAsProps(val));
    },
    /**
     * Get classes for record metadata field based on classified as
     *
     * @param {array|object|string} value
     */
    getClasses(value) {
      let classes = [];
      // add class for chronological lists
      if (getClassifiedAs(value, aat.CHRONOLOGIES).length) {
        classes.push("m-record-metadata-field--chronology");
      }
      return classes;
    },
    /**
     * gets the field label (localized)
     *
     * @param {string} key
     */
    getLabel(key) {
      return this.translate("field_names." + key);
    }
  }
};
</script>

<style lang="scss">
@import "./index";
</style>
